import React, { useContext, useEffect, useState } from "react";
import { useSnackbar } from "react-simple-snackbar";
import { CandidateContext } from "../../../../context/CandidateContext";
import { API } from "../../../../http-common";
import { API_URL, GROUP } from "../../../../constants";
import { BILLING_SOCIETY_OPTIONS, CERTIFICATES_CONFIG, getIibbJurisdiccionMode, IIBB_JURISDICCION_MODE } from "../../../../helpers/uploads";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { listIibbCategory, listIibbCondicion, listProvince } from "../../../../helpers/ListOptions";
import '../../../../assets/css/data_purchases.css';

export const DataPurchases = ({ user_role }) => {
  const { candidateData, refreshCandidateData, isSubUser } = useContext(CandidateContext);

  const [isEditing, setIsEditing] = useState(true);
  const [working, setWorking] = useState(false);

  const [dataPurchases, setDataPurchases] = useState({
    billing_societies: [],
    iigg_exento: "",
    sf_exento: "",
    bsas_exento: "",
    caba_exento: "",
    is_iva_exento: "",
    iigg_agente_retencion: "",
    iibb_condicion: "",
    iibb_category: "",
    iibb_jurisdicciones: [],
    email_retenciones: ["", "", ""],
  });

  const [certificateFiles, setCertificateFiles] = useState({
    iigg_exento: 0,
    sf_exento: 0,
    bsas_exento: 0,
    caba_exento: 0,
  });

  const optionsError = {
    position: "bottom-right",
    style: {
      backgroundColor: "var(--bs-red)",
      border: "0",
      color: "#fff",
      fontSize: "20px",
      textAlign: "center",
      marginBottom: "5rem",
    },
    closeStyle: {
      color: "#fff",
      fontSize: "16px",
      marginRight: "1rem",
    },
  };

  const optionsSuccess = {
    position: "bottom-right",
    style: {
      backgroundColor: "var(--bs-green)",
      border: "0",
      color: "#fff",
      fontSize: "20px",
      textAlign: "center",
      marginBottom: "5rem",
    },
    closeStyle: {
      color: "#fff",
      fontSize: "16px",
      marginRight: "1rem",
    },
  };

  const [openSnackbarSuccess] = useSnackbar(optionsSuccess);
  const [openSnackbarError] = useSnackbar(optionsError);

  const getUploadId = (value) => {
    if (!value) return 0;

    if (typeof value === "number") return value;

    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    if (typeof value === "object") {
      return (
        value.id ||
        value.data?.id ||
        value.data ||
        value.file_id ||
        value.upload_id ||
        value.id_upload ||
        value.dataValues?.id ||
        0
      );
    }

    return 0;
  };

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;

    if (typeof value === "string") {
      if (value.trim() === "") return [];

      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    return [];
  };

  const getEmails = (emails) => {
    const normalizedEmails = normalizeArray(emails);

    return [
      normalizedEmails[0] || "",
      normalizedEmails[1] || "",
      normalizedEmails[2] || "",
    ];
  };

  // La opción "Régimen simplificado" (id 5) fue eliminada de listIibbCondicion.
  // Si el candidato la tiene guardada, no la dejamos seleccionada: forzamos
  // que quede en blanco para que se elija una opción vigente.
  const normalizeIibbCondicion = (value) => {
    if ( value === "" || value === null || value === undefined) return "";
      return Number(value) === 5 ? "" : value;
  }

  useEffect(() => {
    if (!candidateData?.id) return;

    setDataPurchases({
      billing_societies: normalizeArray(candidateData.billing_societies),
      iigg_exento: candidateData.iigg_exento ?? "",
      sf_exento: candidateData.sf_exento ?? "",
      bsas_exento: candidateData.bsas_exento ?? "",
      caba_exento: candidateData.caba_exento ?? "",
      is_iva_exento: candidateData.is_iva_exento ?? "",
      iigg_agente_retencion: candidateData.iigg_agente_retencion ?? "",
      iibb_condicion: normalizeIibbCondicion(candidateData.iibb_condicion),
      iibb_category: candidateData.iibb_category ?? "",
      iibb_jurisdicciones: normalizeJurisdicciones(candidateData.iibb_jurisdicciones),
      email_retenciones: getEmails(candidateData.email_retenciones),
    });

    setCertificateFiles({
      iigg_exento: getUploadId(candidateData.iigg_exento_file),
      sf_exento: getUploadId(candidateData.sf_exento_file),
      bsas_exento: getUploadId(candidateData.bsas_exento_file),
      caba_exento: getUploadId(candidateData.caba_exento_file),
      iibb_category: getUploadId(candidateData.iibb_category_file)
    });
  }, [candidateData]);

  const getAdjustedJurisdicciones = (newCategory, currentJurisdicciones) => {
    const mode = getIibbJurisdiccionMode(newCategory);

    if (mode === IIBB_JURISDICCION_MODE.LOCKED_TO_DOMICILE) {
      const domicileProvince = listProvince.find(
        (p) => p.name.toLowerCase() === String(candidateData?.province ?? "").toLowerCase()
      );
      return domicileProvince ? [domicileProvince.id] : [];
    }

    if (mode === IIBB_JURISDICCION_MODE.SINGLE) {
      const current = normalizeArray(currentJurisdicciones);
      return current.length > 0 ? [current[0]] : [];
    }

    return normalizeArray(currentJurisdicciones);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    if (name === "iibb_category" && Number(value) === 4) {
      const domicileProvince = listProvince.find(
        (p) => p.name.toLowerCase() === String(candidateData?.province ?? "").toLowerCase()
      );

      if (!domicileProvince) {
        openSnackbarError(
          "No se puede seleccionar 'No Inscripto': la Provincia (domicilio comercial) no está completa o no es válida."
        );
        return;
      }
    }

    setDataPurchases((prev) => {
      const updated = {
        ...prev,
        [name]: value === "" ? "" : Number(value),
      };

      if (name === "iibb_category") {
        updated.iibb_jurisdicciones = getAdjustedJurisdicciones(value, prev.iibb_jurisdicciones);
      }

      return updated;
    });

    if (value === "0" || value === "") {
      setCertificateFiles((prev) => ({
        ...prev,
        [name]: 0,
      }));
    }
  };

  const resetForm = () => {
    setDataPurchases({
      billing_societies: normalizeArray(candidateData.billing_societies),
      iigg_exento: candidateData.iigg_exento ?? "",
      sf_exento: candidateData.sf_exento ?? "",
      bsas_exento: candidateData.bsas_exento ?? "",
      caba_exento: candidateData.caba_exento ?? "",
      is_iva_exento: candidateData.is_iva_exento ?? "",
      iigg_agente_retencion: candidateData.iigg_agente_retencion ?? "",
      iibb_condicion: normalizeIibbCondicion(candidateData.iibb_condicion),
      iibb_category: candidateData.iibb_category ?? "",
      iibb_jurisdicciones: normalizeJurisdicciones(candidateData.iibb_jurisdicciones),
      email_retenciones: getEmails(candidateData.email_retenciones)
    });

    setCertificateFiles({
      iigg_exento: getUploadId(candidateData.iigg_exento_file),
      sf_exento: getUploadId(candidateData.sf_exento_file),
      bsas_exento: getUploadId(candidateData.bsas_exento_file),
      caba_exento: getUploadId(candidateData.caba_exento_file),
      iibb_category: getUploadId(candidateData.iibb_category_file)
    });
  };

  const handleEmailChange = (index, value) => {
    setDataPurchases((prev) => {
      const emails = [...(prev.email_retenciones || ["", "", ""])];
      emails[index] = value;

      return {
        ...prev,
        email_retenciones: emails,
      };
    });
  };

  const handleArrayChange = (field, option) => {
    setDataPurchases((prev) => {
      const current = normalizeArray(prev[field]);
      const exists = current.includes(option);

      return {
        ...prev,
        [field]: exists
          ? current.filter((item) => item !== option)
          : [...current, option],
      };
    });
  };

  const normalizeJurisdicciones = (value) => {
    const arr = normalizeArray(value);

    return arr.map((item) => {
      if (typeof item === "number") return item;

      if (typeof item === "string" && !isNaN(Number(item)) && item.trim() !== "") {
        return Number(item);
      }

      const match = listProvince.find((p) => p.name === item);
      return match ? match.id : null;
    }).filter((item) => item !== null);
  };

  const handleJurisdiccionChange = (option) => {
    const mode = getIibbJurisdiccionMode(dataPurchases.iibb_category);

    if (mode === IIBB_JURISDICCION_MODE.LOCKED_TO_DOMICILE) return;

    if (mode === IIBB_JURISDICCION_MODE.SINGLE) {
      setDataPurchases((prev) => ({
        ...prev,
        iibb_jurisdicciones: [option.id],
      }));
      return;
    }

    setDataPurchases((prev) => {
      const current = normalizeArray(prev.iibb_jurisdicciones).map(Number);
      const exists = current.includes(option.id);
      return {
        ...prev,
        iibb_jurisdicciones: exists
          ? current.filter((item) => item !== option.id)
          : [...current, option.id],
      };
    });
  };

  const uploadCertificate = async (e, certificate) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      const dataForm = new FormData();
      dataForm.append("afip", file);

      const res = await API.post(
        `/prospects/uploads/afip/${certificate.documentType}/${candidateData.id}`,
        dataForm,
        { timeout: 10000 },
      );

      const fileId =
        getUploadId(res.data?.data?.[0]) ||
        getUploadId(res.data?.data) ||
        getUploadId(res.data);

      if (fileId) {
        setCertificateFiles((prev) => ({
          ...prev,
          [certificate.name]: fileId,
        }));
      }

      setDataPurchases((prev) => ({
        ...prev,
        [certificate.name]: 1,
      }));

      if (refreshCandidateData) {
        await refreshCandidateData();
      }

      openSnackbarSuccess("Certificado cargado correctamente");
      e.target.value = "";
    } catch (error) {
      console.log(error);
      openSnackbarError("Error al cargar certificado");
    }
  };

  const deleteCertificate = async (fileId, fieldName) => {
    try {
      await API.post(`/prospects/uploads/delete/${fileId}/${candidateData.id}`);

      setCertificateFiles((prev) => ({
        ...prev,
        [fieldName]: 0,
      }));

      if (refreshCandidateData) {
        await refreshCandidateData();
      }

      openSnackbarSuccess("Archivo eliminado");
    } catch (error) {
      console.log(error);
      openSnackbarError("Error al eliminar archivo");
    }
  };

  const savePurchasesData = async (e) => {
    e.preventDefault();

    try {
      setWorking(true);

      await API.post(
        `/prospects/update_prospect/${candidateData.id}`,
        dataPurchases,
      );

      if (refreshCandidateData) {
        await refreshCandidateData();
      }

      openSnackbarSuccess("Datos guardados");
      setIsEditing(false);
    } catch (error) {
      console.log(error);
      openSnackbarError("No se pudieron guardar los datos");
    } finally {
      setWorking(false);
    }
  };

  const renderCertificate = (certificate) => {
    const fieldName = certificate.name;
    const fieldValue = String(dataPurchases[fieldName]);

    const shouldShowCertificate =
      fieldName === "iibb_category"
        ? ["1", "3", "5"].includes(fieldValue)
        : fieldValue === "1";

    if (!shouldShowCertificate) return null;

    const fileId = getUploadId(certificateFiles[fieldName]);

    if (fileId > 0) {
      return (
        <>
          <a
            href={`${API_URL}/prospects/${candidateData.id}/file/${fileId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn blue_btn balanz_btn_longx me-2"
          >
            Ver certificado
          </a>

          {isEditing && (
            <button
              type="button"
              className="btn"
              onClick={() => deleteCertificate(fileId, fieldName)}
            >
              <FontAwesomeIcon icon={faTrash} style={{color: 'red'}}/>
            </button>
          )}
        </>
      );
    }

    return (
      <>
        <input
          hidden
          type="file"
          id={`${fieldName}_file`}
          onChange={(e) => uploadCertificate(e, certificate)}
        />

        <button
          type="button"
          className="btn blue_btn balanz_btn_longx"
          disabled={!isEditing}
          onClick={() => {
            if (isEditing) {
              document.getElementById(`${fieldName}_file`).click();
            }
          }}
        >
          Cargar certificado
        </button>
      </>
    );
  };

  if (isSubUser && !candidateData.invoice_flag) {
    return null;
  }

  if (![...GROUP.EQ_DIRECTORES, ...GROUP.EQ_SOPORTE].includes(user_role)) {
    return null;
  }

  const iibbCategoryCertificate = CERTIFICATES_CONFIG.find(
    (item) => item.name === "iibb_category"
  );

  return (
    <div className="col-md-12 item mb-4">
      <div className="row header_data_section">DATOS ADMINISTRATIVOS</div>

      <form onSubmit={savePurchasesData}>
        <div className="col-md-12 mb-4">
          <label className="data_label blue_data d-block label_style_purchases">
            ¿A qué sociedad se dirigirá la factura? (Puede ser más de una)
          </label>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              marginTop: "6px",
            }}
          >
            {BILLING_SOCIETY_OPTIONS.map((option) => (
              <label
                key={option}
                className="blue_data"
                style={{ display: "flex", alignItems: "center", margin: 0 }}
              >
                <input
                  type="checkbox"
                  style={{ marginRight: "8px" }}
                  disabled={!isEditing}
                  checked={normalizeArray(dataPurchases.billing_societies).includes(option)}
                  onChange={() =>
                    handleArrayChange("billing_societies", option)
                  }
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div
          className="row"
          style={{ marginTop: "18px", marginBottom: "24px" }}
        >
          {CERTIFICATES_CONFIG.filter((item) => item.name !== "iibb_category").map((item) => (
            <div key={item.name} className="col-md-6 mb-4">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <label
                  className="data_label blue_data"
                  style={{ minWidth: "190px", marginBottom: 0 }}
                >
                  {item.label}
                </label>

                <select
                  name={item.name}
                  value={dataPurchases[item.name]}
                  onChange={handleSelectChange}
                  disabled={!isEditing}
                  className="form-control"
                  style={{ width: "150px" }}
                >
                  <option value="">Seleccione...</option>
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>

                <div>{renderCertificate(item)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="col-md-12 mb-4" style={{ marginTop: "8px" }}>
          <div className="row" style={{ marginTop: "8px" }}>

          <div className="col-md-3 mb-4">
            <label className="data_label blue_data d-block label_style_purchases">
              IIGG Agente Retención
            </label>

            <select
              name="iigg_agente_retencion"
              value={dataPurchases.iigg_agente_retencion}
              onChange={handleSelectChange}
              disabled={!isEditing}
              className="form-control"
            >
              <option value="">Seleccione...</option>
              <option value="1">Sí</option>
              <option value="0">No</option>
            </select>
          </div>

          <div className="col-md-3 mb-4">
            <label className="data_label blue_data d-block label_style_purchases">
              Exento IVA
            </label>

            <select
              name="is_iva_exento"
              value={dataPurchases.is_iva_exento}
              onChange={handleSelectChange}
              disabled={!isEditing}
              className="form-control"
            >
              <option value="">Seleccione...</option>
              <option value="1">Sí</option>
              <option value="0">No</option>
            </select>
          </div>

          <div className="col-md-3 mb-4">
            <label className="data_label blue_data d-block label_style_purchases">
              IIBB Condición
            </label>

            <select
              name="iibb_condicion"
              value={dataPurchases.iibb_condicion}
              onChange={handleSelectChange}
              disabled={!isEditing}
              className="form-control"
            >
              <option value="">Seleccione...</option>

              {listIibbCondicion.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {Number(candidateData?.iibb_condicion) === 5 && dataPurchases.iibb_condicion === "" && (
              <small className="text-danger d-block mt-1">
                La opción anteriormente seleccionada ya no está disponible. Por favor, elegí una nueva.
              </small>
            )}
          </div>

          <div className="col-md-3 mb-4">
            <label className="data_label blue_data d-block label_style_purchases">
              IIBB Categoría
            </label>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <select
                name="iibb_category"
                value={dataPurchases.iibb_category}
                onChange={handleSelectChange}
                disabled={!isEditing}
                className="form-control"
              >
                <option value="">Seleccione...</option>

                {listIibbCategory.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <div>{renderCertificate(iibbCategoryCertificate)}</div>
            </div>
          </div>

        </div>
          <label className="data_label blue_data d-block label_style_purchases">
            IIBB Jurisdicciones
          </label>
          {getIibbJurisdiccionMode(dataPurchases.iibb_category) === IIBB_JURISDICCION_MODE.LOCKED_TO_DOMICILE && (
            <small className="text-muted d-block mb-2">
              Categoría "No Inscripto": la jurisdicción se fija automáticamente según el domicilio comercial.
            </small>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "10px",
              marginTop: "6px",
            }}
          >
            {listProvince.map((option) => {
              const mode = getIibbJurisdiccionMode(dataPurchases.iibb_category);
              const isLocked = mode === IIBB_JURISDICCION_MODE.LOCKED_TO_DOMICILE;
              const isChecked = normalizeArray(dataPurchases.iibb_jurisdicciones)
                .map(Number)
                .includes(option.id);

              if (mode === IIBB_JURISDICCION_MODE.SINGLE) {
                return (
                  <label
                    key={option.id}
                    className="blue_data"
                    style={{ display: "flex", alignItems: "center", margin: 0 }}
                  >
                    <input
                      type="radio"
                      name="iibb_jurisdiccion_single"
                      style={{ marginRight: "8px" }}
                      disabled={!isEditing}
                      checked={isChecked}
                      onChange={() => handleJurisdiccionChange(option)}
                    />
                    {option.name}
                  </label>
                );
              }

              return (
                <label
                  key={option.id}
                  className="blue_data"
                  style={{ display: "flex", alignItems: "center", margin: 0 }}
                >
                  <input
                    type="checkbox"
                    style={{ marginRight: "8px" }}
                    disabled={!isEditing || isLocked}
                    checked={isChecked}
                    onChange={() => handleJurisdiccionChange(option)}
                  />
                  {option.name}
                </label>
              );
            })}
          </div>
        </div>

        <div className="col-md-12 mb-3" style={{ marginTop: "14px" }}>
          <label className="data_label blue_data d-block label_style_purchases">
            Emails para retenciones
          </label>
          <small className="text-muted">
            Para recibir Retenciones de Pago (mínimo 1, máximo 3)
          </small>
        </div>

        <div className="row">
          <div className="col-md-4">
            <input
              className="form-control"
              value={dataPurchases.email_retenciones?.[0] || ""}
              onChange={(e) => handleEmailChange(0, e.target.value)}
              disabled={!isEditing}
              placeholder="Email principal"
              type="email"
            />
          </div>

          <div className="col-md-4">
            <input
              className="form-control"
              value={dataPurchases.email_retenciones?.[1] || ""}
              onChange={(e) => handleEmailChange(1, e.target.value)}
              disabled={!isEditing}
              placeholder="Email secundario"
              type="email"
            />
          </div>

          <div className="col-md-4">
            <input
              className="form-control"
              value={dataPurchases.email_retenciones?.[2] || ""}
              onChange={(e) => handleEmailChange(2, e.target.value)}
              disabled={!isEditing}
              placeholder="Email adicional"
              type="email"
            />
          </div>
        </div>

        <div className="text-end mt-4">
          {!isEditing ? (
            <button
              type="button"
              className="btn blue_btn balanz_btn"
              onClick={() => setIsEditing(true)}
            >
              Editar datos
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => {
                  resetForm();
                  setIsEditing(false);
                }}
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={working}
                className="btn blue_btn balanz_btn"
              >
                Guardar
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};
